/**
 * FilterChips.tsx — the applied-filter summary for a list panel.
 *
 * Renders each active filter as a compact, removable chip so the DATA leads and
 * the operator can see (and drop) what's narrowing the list without expanding
 * the full filter form. Purely presentational: the parent owns the query and
 * supplies each chip's removal handler.
 *
 * Accessibility: the chip row is a list; every remove control is a real
 * <button> with a descriptive aria-label (never an icon alone) and a visible
 * focus ring (WCAG 2.1.1, 4.1.2).
 */

export interface FilterChip {
  /** Stable identity for React keys. */
  key: string;
  /** Human-readable, already-localised chip text. */
  label: string;
  /** Localised aria-label for the remove button (names the filter). */
  removeLabel: string;
  onRemove: () => void;
}

export interface FilterChipsProps {
  chips: FilterChip[];
  testid?: string;
}

export const FilterChips = ({ chips, testid = 'filter-chips' }: FilterChipsProps) => {
  if (chips.length === 0) return null;
  return (
    <ul data-testid={testid} className="flex flex-wrap items-center gap-2">
      {chips.map((chip) => (
        <li key={chip.key}>
          <span
            data-testid={`${testid}-chip`}
            className="inline-flex items-center gap-1.5 rounded-full bg-surface-3 py-0.5 pl-2.5 pr-1 text-meta text-ink-1 ring-1 ring-inset ring-ink-3/20"
          >
            <span>{chip.label}</span>
            <button
              type="button"
              onClick={chip.onRemove}
              aria-label={chip.removeLabel}
              data-testid={`${testid}-remove`}
              className="inline-flex h-4 w-4 items-center justify-center rounded-full text-ink-3 hover:bg-surface-1 hover:text-ink-1 focus:outline-none focus:ring-2 focus:ring-outline-focus"
            >
              <span aria-hidden="true">×</span>
            </button>
          </span>
        </li>
      ))}
    </ul>
  );
};
